import styled from "@emotion/styled";
import { map, slice } from "lodash-es";
import { useEffect, useState } from "react";

import {
  MDSButton,
  MDSButtonVariant,
  MDSSkeleton,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
} from "@/mds";
import { AgentRoutineCard } from "@/pages/agent/routines/agent-routine-card";
import type { RoutineRowViewModel } from "@/pages/agent/routines/agent-routines-page.utils";

export const AgentRoutinesSection = ({
  routineRows,
  dataLoaded,
  hasActiveFilters,
  isRoutineSharingEnabled,
  queryKey,
  onOpenActivities,
}: {
  routineRows: RoutineRowViewModel[];
  dataLoaded: boolean;
  hasActiveFilters: boolean;
  isRoutineSharingEnabled: boolean;
  queryKey: string;
  onOpenActivities: ({ routineId }: { routineId: string }) => void;
}) => {
  const [visibleRoutineCount, setVisibleRoutineCount] = useState(AGENT_ROUTINE_PAGE_SIZE);
  const visibleRoutineRows = slice(routineRows, 0, visibleRoutineCount);

  useEffect(() => {
    setVisibleRoutineCount(AGENT_ROUTINE_PAGE_SIZE);
  }, [queryKey]);

  const emptyStateLabel = hasActiveFilters ? "No matching routines" : "No routines";

  if (!dataLoaded && routineRows.length === 0) {
    return (
      <StyledLoadingState aria-label="Loading routines">
        <MDSSkeleton height={18} width="40%" />
        <MDSSkeleton height={14} width="70%" />
      </StyledLoadingState>
    );
  }

  if (routineRows.length === 0) {
    return (
      <StyledEmptyState>
        <MDSText color={MDSTextColor.Secondary} variant={MDSTextVariant.TextS}>
          {emptyStateLabel}
        </MDSText>
      </StyledEmptyState>
    );
  }

  return (
    <StyledRoutineSection>
      <StyledRoutineGrid aria-label="Routines" role="list">
        {map(visibleRoutineRows, routineRow => (
          <AgentRoutineCard
            key={routineRow.routine.id}
            isRoutineSharingEnabled={isRoutineSharingEnabled}
            routineRow={routineRow}
            onOpenActivities={onOpenActivities}
          />
        ))}
      </StyledRoutineGrid>

      {visibleRoutineRows.length < routineRows.length ? (
        <StyledLoadMoreButton
          variant={MDSButtonVariant.Transparent}
          onPress={() => {
            setVisibleRoutineCount(currentCount =>
              Math.min(currentCount + AGENT_ROUTINE_PAGE_SIZE, routineRows.length)
            );
          }}
        >
          Show more routines
        </StyledLoadMoreButton>
      ) : null}
    </StyledRoutineSection>
  );
};

const AGENT_ROUTINE_PAGE_SIZE = 20;

const StyledRoutineSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
  min-height: 0;
`;

const StyledRoutineGrid = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing[5]};
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const StyledLoadMoreButton = styled(MDSButton)`
  align-self: center;
`;

const StyledEmptyState = styled.div`
  background: ${({ theme }) => theme.colors.background.input.chrome.default};
  border: 1px solid ${({ theme }) => theme.colors.border.surface};
  border-radius: 12px;
  padding: ${({ theme }) => theme.spacing[6]} ${({ theme }) => theme.spacing[5]};
`;

const StyledLoadingState = styled(StyledEmptyState)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
`;
